import { useRequiredContext } from "@redotech/react-util/context";
import UploadCloudIcon from "@redotech/redo-icons/upload-cloud-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoAdminClientContext } from "../../../../../client/context";
import { ImportPlatform } from "../types";
import { useMacroImport } from "../use-macro-import";

interface MacrosTabProps {
  teamId: string | undefined;
  platform: ImportPlatform;
}

const PLATFORM_DISPLAY_NAMES: Record<ImportPlatform, string> = {
  [ImportPlatform.GORGIAS]: "Gorgias",
  [ImportPlatform.GORGIAS_NATIVE]: "Gorgias Native",
  [ImportPlatform.ZENDESK]: "Zendesk",
  [ImportPlatform.REAMAZE]: "Reamaze",
  [ImportPlatform.RICHPANEL]: "RichPanel",
};

export const MacrosTab = memo(function MacrosTab({
  teamId,
  platform,
}: MacrosTabProps) {
  const client = useRequiredContext(RedoAdminClientContext);
  const { file, loading, handleFileChange, handleSubmit } = useMacroImport({
    teamId,
    platform,
    client,
  });

  const platformDisplayName = PLATFORM_DISPLAY_NAMES[platform];
  const inputId = `${platform}-macro-upload`;

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" textColor="secondary">
        Upload a CSV of {platformDisplayName} macros. You can upload one file at
        a time.
      </Text>
      {platform === ImportPlatform.GORGIAS && (
        <RedoCollapseSubsection title="CSV Format Instructions">
          <Flex dir="column" gap="sm" pl="lg">
            <Text fontSize="sm" textColor="secondary">
              Your CSV file must include the following columns:
            </Text>
            <Text fontSize="sm" textColor="secondary">
              <li>
                <strong>macros__title</strong> - The name of the macro
                (required)
              </li>
              <li>
                <strong>macros__actions__value</strong> - The macro content/text
                (required)
              </li>
              <li>
                <strong>tags</strong> - Comma-separated tags (optional)
              </li>
              <li>
                <strong>macros__id</strong> - Macro ID from Gorgias (optional,
                not used)
              </li>
            </Text>
            <Text fontSize="sm" textColor="secondary">
              <strong>To export from Gorgias:</strong>
            </Text>
            <Text fontSize="sm" textColor="secondary">
              <li>• Go to Settings &gt; Productivity &gt; Macros in Gorgias</li>
              <li>• Export your macros to CSV</li>
              <li>
                • The exported file should already have the correct column
                format
              </li>
              <li>
                • Macros without a title or content will be skipped during
                import
              </li>
              <li>• Duplicate macros (same name) will be skipped</li>
            </Text>
          </Flex>
        </RedoCollapseSubsection>
      )}
      {platform === ImportPlatform.GORGIAS_NATIVE && (
        <RedoCollapseSubsection title="CSV Format Instructions">
          <Flex dir="column" gap="sm" pl="lg">
            <Text fontSize="sm" textColor="secondary">
              Use the native CSV format exported directly from Gorgias. Your CSV
              file must include the following columns:
            </Text>
            <Text fontSize="sm" textColor="secondary">
              <li>
                <strong>name</strong> - The name of the macro (required)
              </li>
              <li>
                <strong>body_text</strong> - The macro content/text (required)
              </li>
              <li>
                <strong>tags</strong> - Comma-separated tags (optional)
              </li>
              <li>
                <strong>language</strong>, <strong>usage</strong>,{" "}
                <strong>updated</strong>, <strong>created</strong>,{" "}
                <strong>id</strong>, <strong>archived</strong>,{" "}
                <strong>subject</strong> - Ignored during import
              </li>
            </Text>
            <Text fontSize="sm" textColor="secondary">
              <strong>To export from Gorgias:</strong>
            </Text>
            <Text fontSize="sm" textColor="secondary">
              <li>• Go to Settings &gt; Productivity &gt; Macros in Gorgias</li>
              <li>• Export your macros to CSV</li>
              <li>• Upload the exported file directly without modification</li>
              <li>
                • Macros without a name or body text will be skipped during
                import
              </li>
              <li>• Duplicate macros (same name) will be skipped</li>
            </Text>
          </Flex>
        </RedoCollapseSubsection>
      )}
      <Flex align="center" gap="md">
        <label htmlFor={inputId}>
          <RedoButton
            disabled={loading}
            hierarchy="secondary"
            IconLeading={UploadCloudIcon}
            onClick={() => document.getElementById(inputId)?.click()}
            size="md"
            text="Choose File"
            theme="normal"
          />
        </label>
        <input
          accept=".csv"
          id={inputId}
          onChange={handleFileChange}
          style={{ display: "none" }}
          type="file"
        />
        {file && (
          <Text fontSize="sm" textColor="secondary">
            {file.name}
          </Text>
        )}
      </Flex>
      <div>
        <RedoButton
          disabled={!file || loading}
          hierarchy="primary"
          IconLeading={UploadCloudIcon}
          onClick={handleSubmit}
          pending={loading}
          size="md"
          text="Import Macros"
          theme="normal"
        />
      </div>
    </Flex>
  );
});

export function createMacrosTab(platform: ImportPlatform) {
  return memo(function PlatformMacrosTab({
    teamId,
  }: {
    teamId: string | undefined;
  }) {
    return <MacrosTab platform={platform} teamId={teamId} />;
  });
}
